import React, { useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  Chip,
  Avatar,
  ListItemText,
  ListItem,
  ListItemIcon,
  List,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ThemeContext } from '../Context/ThemeContext';
import SettingIcon from './images/setting_icon.svg';
import FlowIcon from './images/flow_icon.svg';
import { styled } from '@mui/material/styles';
import OutlinedInput from '@mui/material/OutlinedInput';
import RuleIcon from '@mui/icons-material/Rule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useSelector } from 'react-redux';

const OP_OPTIONS = [
  { value: 'eq', label: '= Equals' },
  { value: 'neq', label: '≠ Not equal' },
  { value: 'gt', label: '> Greater than' },
  { value: 'gte', label: '≥ Greater than or equal' },
  { value: 'lt', label: '< Less than' },
  { value: 'lte', label: '≤ Less than or equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'in', label: 'In (comma separated)' },
  { value: 'exists', label: 'Exists (no value)' },
];

const BLANK_DECISION = {
  sourceEntity: '',
  sourceField: '',
  operator: 'gt',
  value: '',
  truePathLabel: '',
  falsePathLabel: '',
};

const DecisionConfigurationModal = ({
  selectedNode,
  setSelectedNode,
  updateNodeData,
  onConfigUpdate,
  onSaveAndNext,
  sourceResolved = [],
  // destinationOptions = [],
  connectedDestinations,
  onClose,
}) => {
  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext);

  if (!selectedNode) return null;

  // const destIds = connectedDestinations?.destIds || [];
  // const destNames = connectedDestinations?.destNames || [];
  // const destOptions = destIds.map((id, i) => ({
  //   id,
  //   label: destNames[i] || id,
  // }));

  // const [flowOrder, setFlowOrder] = useState(destIds);
  // const [flowOrders, setFlowOrders] = useState([
  //   Array(destIds.length).fill(''),
  // ]);

  const limitedIds = useMemo(
    () => (connectedDestinations?.destIds || []),
    [connectedDestinations],
  );

  // const idToLabel = (id) => {
  //   const i = limitedIds.indexOf(id);
  //   return i >= 0 ? limitedNames[i] || id : id;
  // };

  const limitedNames = useMemo(
    () => (connectedDestinations?.destNames || []),
    [connectedDestinations],
  );
  const destOptions = limitedIds.map((id, i) => ({
    id,
    label: limitedNames[i] || id,
  }));
  const [flowOrders, setFlowOrders] = useState([
    Array(limitedIds.length).fill(''),
  ]);

  console.log('limitedIds.length', limitedIds.length);

  const [conditions, setConditions] = useState([{ ...BLANK_DECISION }]);
  const [activeIdx, setActiveIdx] = useState(0);

  const [errorMsg, setErrorMsg] = useState('');

  const decision = conditions[activeIdx] || BLANK_DECISION;

  const workflowNodes = useSelector((s) => s.workflows?.nodes) || [];

  const connIdByDestId = useMemo(() => {
    const map = {};
    for (const n of workflowNodes) {
      if (n?.type === 'destinationNode' || n?.type === 'destination') {
        // support both shapes
        const cfg = n.data?.config || n.config || {};
        const cid = cfg?.connection?.conn_id;
        if (cid) map[n.id] = cid;
      }
    }
    return map;
  }, [workflowNodes]);

  const withConn = (destId) => {
    if (!destId) return '';
    const cid = connIdByDestId[destId];
    return cid ? `${destId}__${cid}` : destId;
  };

  const destDisplayById = useMemo(() => {
    const map = {};
    (workflowNodes || []).forEach((n) => {
      if (n?.type === 'destinationNode' || n?.type === 'destination') {
        const cfg = n.data?.config || n.config || {};
        // let baseLabel = cfg?.label || n.data?.label || n.label || n.id;

        // baseLabel = baseLabel.replace(/\s*\(Destination\)$/i, '');

        const connName = cfg?.connection?.conn_name || '';
        map[n.id] = connName;
        console.log('baseLabel::', n.id);
      }
    });
    return map;
  }, [workflowNodes]);

  console.log('destDisplayById::', destDisplayById);

  // const requiredEntities = useMemo(() => {
  //   const set = new Set();
  //   (Array.isArray(sourceResolved) ? sourceResolved : []).forEach((it) => {
  //     if (it?.entity) set.add(String(it.entity));
  //   });
  //   return Array.from(set);
  // }, [sourceResolved]);

  const requiredEntities = useMemo(() => {
    const set = new Set();
    (Array.isArray(sourceResolved) ? sourceResolved : []).forEach((it) => {
      if (it?.entity) set.add(String(it.entity));
    });
    if (set.size === 0) {
      const all = sourceResolved || [];
      all.forEach((it) => {
        if (it?.entity) set.add(String(it.entity));
      });
    }
    return Array.from(set);
  }, [sourceResolved]);

  const visibleConditions = useMemo(
    () =>
      conditions.filter(
        (c) => !c.sourceEntity || requiredEntities.includes(c.sourceEntity),
      ),
    [conditions, requiredEntities],
  );

  const entityOptions = useMemo(
    () => requiredEntities.map((e) => ({ value: e, label: e })),
    [requiredEntities],
  );

  const decisionNeedsValue = (op) => op !== 'exists';

  const isConditionComplete = (c) => {
    if (!c?.sourceEntity || !c?.sourceField || !c?.operator) return false;
    if (decisionNeedsValue(c.operator))
      return String(c.value ?? '').trim() !== '';
    return true;
  };

  const allEntitiesCovered = useMemo(() => {
    return requiredEntities.every((ent) =>
      conditions.some((c) => c.sourceEntity === ent && isConditionComplete(c)),
    );
  }, [requiredEntities, conditions]);

  // const currentFlow = flowOrders[activeIdx] ?? Array(destIds.length).fill('');
  const currentFlow =
    flowOrders[activeIdx] ?? Array(limitedIds.length).fill('');

  // const allDestinationsSelected = useMemo(() => {
  //   const ids = limitedIds;
  //   const order = currentFlow;
  //   if (!ids.length || order.length !== ids.length) return false;
  const allDestinationsSelected = useMemo(() => {
    const ids = limitedIds;
    const order = currentFlow.slice(0, 2);
    if (!ids.length || order.length < 2) return false;
    const nonEmpty = order.every((v) => String(v || '').trim());
    const uniqueAndValid =
      new Set(order).size === order.length &&
      order.every((v) => ids.includes(v));
    return nonEmpty && uniqueAndValid;
  }, [limitedIds, currentFlow]);

  const labelsFilled = useMemo(
    () =>
      conditions.every(
        (c) =>
          String(c.truePathLabel || '').trim() &&
          String(c.falsePathLabel || '').trim(),
      ),
    [conditions],
  );

  // const canSaveNext = allEntitiesCovered && labelsFilled;

  const canSaveNext = allEntitiesCovered && allDestinationsSelected;

  // useEffect(() => {
  //   setFlowOrder(Array(destIds.length).fill(''));
  // }, [selectedNode?.id, destIds.join('|')]);

  useEffect(() => {
    setFlowOrders((prev) =>
      Array(conditions.length)
        .fill(0)
        .map(
          (_, i) =>
            prev[i]?.slice(0, limitedIds.length) ??
            Array(limitedIds.length).fill(''),
        ),
    );
  }, [selectedNode?.id, limitedIds.join('|'), conditions.length]);

  const setCurrentFlow = (updater) =>
    setFlowOrders((prev) => {
      const next = [...prev];
      next[activeIdx] =
        typeof updater === 'function' ? updater(currentFlow) : updater;
      return next;
    });

  const setDecision = (updater) => {
    setConditions((prev) => {
      const next = [...prev];
      const updated =
        typeof updater === 'function' ? updater(next[activeIdx]) : updater;
      next[activeIdx] = updated;
      return next;
    });
  };

  // const allFieldOptions = useMemo(() => {
  //   if (!Array.isArray(sourceResolved)) return [];
  //   return sourceResolved.flatMap((it) => {
  //     const entity = it?.entity;
  //     const fields = Array.isArray(it?.fields?.fields) ? it.fields.fields : [];
  //     return fields.map((f) => ({
  //       key: `${entity}::${f.field_name}`,
  //       entity,
  //       fieldName: f.field_name,
  //       label: f.field_label || f.field_name,
  //     }));
  //   });
  // }, [sourceResolved]);

  const allFieldOptions = useMemo(() => {
    if (!Array.isArray(sourceResolved)) return [];
    return sourceResolved.flatMap((it) => {
      const entity = it?.entity;
      const fields = Array.isArray(it?.fields?.fields) ? it.fields.fields : [];
      return fields.map((f) => ({
        key: `${entity}::${f.field_name}`,
        entity,
        fieldName: f.field_name,
        label: `${entity}.${f.field_label || f.field_name}`, // show origin
      }));
    });
  }, [sourceResolved]);

  const scopedFieldOptions = useMemo(() => {
    if (!decision?.sourceEntity) return [];
    return allFieldOptions.filter((o) => o.entity === decision.sourceEntity);
  }, [allFieldOptions, decision?.sourceEntity]);

  const selectedKey = useMemo(() => {
    if (!decision?.sourceEntity || !decision?.sourceField) return '';
    return `${decision.sourceEntity}::${decision.sourceField}`;
  }, [decision.sourceEntity, decision.sourceField]);

  // const handleFieldChange = (e) => {
  //   const key = e.target.value;
  //   const opt = allFieldOptions.find((o) => o.key === key);
  //   if (!opt) {
  //     setDecision((d) => ({ ...d, sourceEntity: '', sourceField: '' }));
  //     return;
  //   }
  //   setDecision((d) => ({
  //     ...d,
  //     sourceEntity: opt.entity,
  //     sourceField: opt.fieldName,
  //   }));
  // };

  const handleFieldChange = (e) => {
    const key = e.target.value;
    const opt = scopedFieldOptions.find((o) => o.key === key);
    if (!opt) return setDecision((d) => ({ ...d, sourceField: '' }));
    setDecision((d) => ({
      ...d,
      sourceEntity: opt.entity,
      sourceField: opt.fieldName,
    }));
  };

  useEffect(() => {
    const cfg = selectedNode?.data?.config || {};
    const dec = cfg.decision;

    if (dec?.conditions && typeof dec.conditions === 'object') {
      const ordered = Object.keys(dec.conditions)
        .sort((a, b) => {
          const ai = parseInt(a.replace(/[^\d]/g, ''), 10) || 0;
          const bi = parseInt(b.replace(/[^\d]/g, ''), 10) || 0;
          return ai - bi;
        })
        .map((k) => dec.conditions[k]);

      // if (ordered.length) {
      //   setConditions(ordered);
      //   setActiveIdx(0);
      //   return;
      // }

      if (ordered.length) {
        setConditions(ordered);

        // const restored = ordered.map((c) =>
        //   Array.isArray(c.flowOrder)
        //     ? [...c.flowOrder]
        //     : Array(destIds.length).fill('')
        // );
        // setFlowOrders(restored);

        const restored = ordered.map((c) =>
          Array.isArray(c.flowOrder)
            ? [...c.flowOrder].slice(0, limitedIds.length)
            : Array(limitedIds.length).fill(''),
        );
        setFlowOrders(restored);

        setActiveIdx(0);
        return;
      }
    }

    if (dec && !dec.conditions) {
      setConditions([
        {
          sourceEntity: dec.sourceEntity ?? '',
          sourceField: dec.sourceField ?? '',
          operator: dec.operator ?? 'gt',
          value:
            Array.isArray(dec.value) && dec.operator === 'in'
              ? dec.value.join(', ')
              : (dec.value ?? ''),
          truePathLabel: dec.truePathLabel ?? '',
          falsePathLabel: dec.falsePathLabel ?? '',
        },
      ]);
      setActiveIdx(0);
      return;
    }

    setConditions([{ ...BLANK_DECISION }]);
    setActiveIdx(0);
  }, [selectedNode?.id]);

  const handleAddDecision = () => {
    setConditions((prev) => [...prev, { ...BLANK_DECISION }]);
    setActiveIdx((prevIdx) => prevIdx + 1);
  };

  const handleRemoveActive = () => {
    setConditions((prev) => {
      if (prev.length === 1) {
        // keep at least one blank condition
        return [{ ...BLANK_DECISION }];
      }
      const next = prev.filter((_, i) => i !== activeIdx);
      // fix active index
      const newIdx = Math.max(0, activeIdx - 1);
      setActiveIdx(newIdx);
      return next;
    });
  };

  // const buildDecisionJSON = () => {
  //   const normalized = conditions.map((c, i) => ({
  //     sourceEntity: (c.sourceEntity || '').trim(),
  //     sourceField: (c.sourceField || '').trim(),
  //     operator: c.operator,
  //     ...(c.operator !== 'exists' && {
  //       value:
  //         c.operator === 'in'
  //           ? String(c.value || '')
  //               .split(',')
  //               .map((s) => s.trim())
  //               .filter(Boolean)
  //           : c.value,
  //     }),
  //     // ...(flowOrders[i] ? { flowOrder: [...flowOrders[i]] } : {}),
  //     // ...(c.truePathLabel?.trim()
  //     //   ? { truePathLabel: c.truePathLabel.trim() }
  //     //   : {}),
  //     // ...(c.falsePathLabel?.trim()
  //     //   ? { falsePathLabel: c.falsePathLabel.trim() }
  //     //   : {}),

  //   }));
  //   return {
  //     conditions: normalized.reduce(
  //       (acc, it, i) => ((acc[`condition_${i + 1}`] = it), acc),
  //       {}
  //     ),
  //   };
  // };

  const buildDecisionJSON = () => {
    // const normalized = conditions.map((c, i) => {
    const normalized = visibleConditions.map((c) => {
      const idx = conditions.indexOf(c);

      const base = {
        sourceEntity: (c.sourceEntity || '').trim(),
        sourceField: (c.sourceField || '').trim(),
        operator: c.operator,
      };
      if (c.operator !== 'exists') {
        base.value =
          c.operator === 'in'
            ? String(c.value || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : c.value;
      }

      const flow = (flowOrders[idx] || []).slice(0, limitedIds.length);
      // const trueId = flow[0] || '';
      // const falseId = flow[1] || '';

      base.flowOrder = flow;
      base.truePathLabel = withConn(flow[0]);
      base.falsePathLabel = withConn(flow[1]);

      return base;
    });

    return {
      conditions: normalized.reduce((acc, it, idx) => {
        acc[`condition_${idx + 1}`] = it;
        return acc;
      }, {}),
    };
  };

  const handleSave = () => {
    const prevConfig = selectedNode?.data?.config || {};
    const decisionJSON = buildDecisionJSON();

    const updatedConfig = {
      ...prevConfig,
      id: selectedNode.id,
      label: selectedNode?.data?.label || prevConfig.label,
      type: selectedNode.type,
      decision: decisionJSON,
    };

    updateNodeData(selectedNode.id, updatedConfig);
    onConfigUpdate(updatedConfig);
    console.log('Saved decision:', decisionJSON);
    console.log('Saved config:', updatedConfig);
    // console.log('Saved decision:', decisionJSON);
    return updatedConfig;
  };

  // const handleCloseOnly = () => {
  //   handleSave();
  //   setSelectedNode(null);
  // };

  const handleCloseAndSave = () => {
    if (!canSaveNext) {
      console.warn('Decision validation failed:', {
        allEntitiesCovered,
        labelsFilled,
        requiredEntities,
        conditions,
      });
      setErrorMsg(
        !allEntitiesCovered
          ? 'Add ≥1 complete condition for every selected source entity.'
          : 'Select ALL destinations in Flow Path (no blanks or duplicates).',
      );

      return;
    }
    handleSave();
    setSelectedNode(null);

    if (typeof onSaveAndNext === 'function') {
      setTimeout(() => onSaveAndNext(), 0);
    }
  };

  const getContainedButtonStyle = {
    fontSize: '0.738rem',
    backgroundColor: theme.palette.primary.main,
    padding: '0.3rem 0.8rem',
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    borderRadius: 0.5,
    boxShadow: '0px 4px 10px 0px #0000000D',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.03)',
      background: 'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
      boxShadow: '0px 0px 5.5px 0px rgb(94, 133, 205)',
      border: '1px solid transparent',
    },
    '&:active': {
      background: 'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
      boxShadow: '0px 0px 5.5px 0px rgb(94, 133, 205)',
      border: '1px solid transparent',
    },
  };

  const ChipScroller = styled(Box)({
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 6,
    overflowX: 'auto',
    paddingBottom: 4,
    scrollBehavior: 'smooth',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  });

  const ConditionChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== '$active',
  })(({ $active }) => ({
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    cursor: 'pointer',
    '& .MuiChip-label': { padding: '0 8px', fontSize: 12 },
    '& .MuiChip-icon': { fontSize: 16, marginLeft: 2 },
    '& .MuiChip-avatar': {
      width: 18,
      height: 18,
      fontSize: 11,
      backgroundColor: $active ? '#ffffff !important' : '#0150C3 !important',
      color: $active ? '#0150C3' : '#ffffff',
    },
    ...(!$active
      ? { borderColor: '#B9B9B9' }
      : {
          backgroundColor: '#0150C3',
          color: '#fff',
          borderColor: 'transparent',
          '& .MuiChip-icon': { color: '#fff' },
        }),
  }));

  const SmallSelect = styled((props) => (
    <Select
      {...props}
      variant="outlined"
      size="small"
      input={<OutlinedInput />}
    />
  ))({
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      height: 32,
      borderRadius: 4,
    },

    '& .MuiOutlinedInput-input': {
      padding: '4px 10px !important',
      lineHeight: 1.2,
      fontSize: 12,
      boxSizing: 'border-box',
    },

    '& .MuiSelect-select': {
      minHeight: 'unset !important',
      paddingRight: 32,
      display: 'flex',
      alignItems: 'center',
    },

    '& .MuiSelect-icon': {
      fontSize: 18,
      right: 8,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 4,
    },
  });

  const SmallMenuItem = styled(MenuItem)({
    fontSize: 12,
    minHeight: 28,
    lineHeight: 1.2,
    paddingTop: 6,
    paddingBottom: 6,
  });

  const SMALL_MENU_PROPS = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    // keep it attached to the field instead of re-anchoring to viewport edges
    disablePortal: true,
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: 1,
        maxHeight: 240,
        '& .MuiList-root': { py: 0.25 },
        '& .MuiMenuItem-root': {
          minHeight: 28,
          py: 0.5,
          fontSize: 12,
          lineHeight: 1.2,
        },
        '& .MuiMenuItem-gutters': { px: 1 },
      },
    },
    MenuListProps: {
      dense: true,
      sx: { py: 0 },
    },
  };

  const CARD_BG = '#F7F9FF';
  const RADIUS = 8;

  const sharedInputSx = {
    height: '2rem',
    borderRadius: `4px`,
    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B9B9B9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B9B9B9' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#B9B9B9',
    },
    '.MuiSelect-select, .MuiInputBase-input': { padding: '10px 12px' },
  };

  const sectionCardSx = {
    border: `1px solid ${darkMode ? '#333' : '#E5E7EB'}`,
    borderRadius: `${RADIUS}px`,
    p: 2,
    mb: 2,
    background: CARD_BG,
    boxShadow: '0px 0px 4px 0px #00000024',
  };

  const addBtnSx = {
    minWidth: 26,
    height: 26,
    px: 0,
    borderRadius: `4px`,
    backgroundColor: '#0150C3',
    color: '#fff',
    '&:hover': { backgroundColor: '#0141A0' },
  };

  // const labelFor = (value) => {
  //   const i = limitedIds.indexOf(value);
  //   const name = destOptions.find((o) => o.id === value)?.label || value;
  //   return i >= 0 ? `${name}  D${i + 1}` : name;
  // };

  const labelFor = (value) => {
    const i = limitedIds.indexOf(value);
    const name =
      destDisplayById[value] ||
      destOptions.find((o) => o.id === value)?.label ||
      value;
    return i >= 0 ? `${name}` : name;
  };

  const fieldOnly = (s) => (s || '').split('.').pop() || s;

  return (
    <Box
      sx={{
        width: '30%',
        minWidth: 250,
        maxWidth: 325,
        height: '90vh',
        overflowY: 'auto',
        position: 'fixed',
        top: 44,
        right: 0,
        backgroundColor: darkMode ? '#1e1e1e' : theme.palette.background.paper,
        boxShadow: darkMode
          ? '-5px 0 10px rgba(0, 0, 0, 0.5)'
          : '-5px 0 10px rgba(0, 0, 0, 0.2)',
        borderLeft: darkMode
          ? '1px solid #333333'
          : `1px solid ${theme.palette.divider}`,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 19 }}>
          Configuration Panel
        </Typography>
        <Tooltip title="Save & Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={sectionCardSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
              component="img"
              src={SettingIcon}
              alt="Settings"
              sx={{ width: 24, height: 24, mr: 1 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
              Condition Setup
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {conditions.length > 0 && (
              <Tooltip title="Remove active condition">
                <IconButton size="small" onClick={handleRemoveActive}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Add condition">
              <Button size="small" sx={addBtnSx} onClick={handleAddDecision}>
                <AddIcon fontSize="small" sx={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <ChipScroller>
          {conditions.map((c, idx) => {
            if (c.sourceEntity && !requiredEntities.includes(c.sourceEntity))
              return null;

            const active = idx === activeIdx;
            const canDelete = conditions.length > 1;
            return (
              <ConditionChip
                key={idx}
                $active={active}
                variant={active ? 'filled' : 'outlined'}
                size="small"
                icon={<RuleIcon />}
                avatar={<Avatar>{idx + 1}</Avatar>}
                label="Condition"
                clickable
                aria-pressed={active}
                onClick={() => setActiveIdx(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveIdx(idx);
                  }
                }}
                onDelete={handleRemoveActive}
                deleteIcon={canDelete ? <CloseIcon /> : undefined}
                sx={{ mr: 0.5 }}
              />
            );
          })}
        </ChipScroller>

        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
          Source Entity
        </Typography>

        {/* Entity first */}
        <SmallSelect
          fullWidth
          value={decision.sourceEntity || ''}
          onChange={(e) =>
            setDecision((d) => ({
              ...d,
              sourceEntity: e.target.value,
              sourceField: '',
            }))
          }
          MenuProps={SMALL_MENU_PROPS}
          displayEmpty
          sx={{ ...sharedInputSx, mb: 1 }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          {entityOptions.map((opt) => (
            <SmallMenuItem key={opt.value} value={opt.value}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.2,
                }}
              >
                <span>{opt.label}</span>
                {/* <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 12 }}
                >
                  Entity
                </Typography> */}
              </Box>
            </SmallMenuItem>
          ))}
        </SmallSelect>

        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
          Source Field
        </Typography>

        <SmallSelect
          fullWidth
          value={selectedKey}
          onChange={handleFieldChange}
          MenuProps={SMALL_MENU_PROPS}
          displayEmpty
          renderValue={(v) => {
            const opt = scopedFieldOptions.find((o) => o.key === v);
            return opt ? fieldOnly(opt.label) : 'Select';
          }}
          sx={{
            ...sharedInputSx,
            mb: 2,
            '& .MuiOutlinedInput-root,& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '4px !important',
            },
          }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          {scopedFieldOptions.map((opt) => (
            <SmallMenuItem key={opt.key} value={opt.key}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.2,
                }}
              >
                <span>{fieldOnly(opt.label)}</span>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 12 }}
                >
                  {opt.entity}
                </Typography>
              </Box>
            </SmallMenuItem>
          ))}
        </SmallSelect>

        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
          Comparison Operator
        </Typography>
        <SmallSelect
          fullWidth
          value={decision.operator}
          MenuProps={SMALL_MENU_PROPS}
          onChange={(e) =>
            setDecision((d) => ({ ...d, operator: e.target.value }))
          }
          displayEmpty
          sx={{
            ...sharedInputSx,
            '& .MuiOutlinedInput-root': { borderRadius: '4px !important' },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '4px !important',
            },
            mb: decision.operator === 'exists' ? 0 : 2,
          }}
        >
          {OP_OPTIONS.map((op) => (
            <SmallMenuItem key={op.value} value={op.value}>
              {op.label}
            </SmallMenuItem>
          ))}
        </SmallSelect>

        {decision.operator !== 'exists' && (
          <>
            <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
              Comparison Value
            </Typography>
            <TextField
              fullWidth
              placeholder={
                decision.operator === 'in'
                  ? 'e.g., gold, silver, platinum'
                  : 'e.g., 18'
              }
              value={decision.value}
              onChange={(e) =>
                setDecision((d) => ({ ...d, value: e.target.value }))
              }
              sx={{
                borderRadius: 0,
                ...sharedInputSx,

                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '4px !important',
                  overflow: 'hidden',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '6px 10px',
                  lineHeight: 1.2,
                  fontSize: 12,
                },
                '& .MuiOutlinedInput-notchedOutline, & fieldset': {
                  borderRadius: '4px !important',
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Flow Path */}
      {/* <Box sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            component="img"
            src={FlowIcon}
            alt="Settings"
            sx={{ width: 24, height: 24, mr: 1 }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Flow Path
          </Typography>
        </Box>

        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
          Select Destination 1
        </Typography>
        <SmallSelect
          fullWidth
          value={decision.truePathLabel || ''}
          MenuProps={SMALL_MENU_PROPS}
          onChange={(e) =>
            setDecision((d) => ({ ...d, truePathLabel: e.target.value }))
          }
          displayEmpty
          sx={{
            ...sharedInputSx,
            mb: 2,
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          {destinationOptions.map((opt) => (
            <SmallMenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </SmallMenuItem>
          ))}
        </SmallSelect>

        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
          Select Destination 2
        </Typography>
        <SmallSelect
          fullWidth
          value={decision.falsePathLabel || ''}
          MenuProps={SMALL_MENU_PROPS}
          onChange={(e) =>
            setDecision((d) => ({ ...d, falsePathLabel: e.target.value }))
          }
          displayEmpty
          sx={{
            ...sharedInputSx,
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          {destinationOptions.map((opt) => (
            <SmallMenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </SmallMenuItem>
          ))}
        </SmallSelect>
      </Box> */}

      <Box sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            component="img"
            src={FlowIcon}
            alt="Flow"
            sx={{ width: 24, height: 24, mr: 1 }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Flow Path
          </Typography>
        </Box>

        {/* {(currentFlow.length
          ? currentFlow
          : Array(limitedIds.length).fill('')
        ).map((id, idx) => ( */}
        {(currentFlow.length ? currentFlow.slice(0, 2) : Array(2).fill('')).map((id, idx) => (
          <Box key={id || `flow-${idx}`} sx={{ mb: 1.5 }}>
            <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: 14 }}>
              {idx === 0 ? 'Pass Route' : 'Fail Route'}
            </Typography>

            <SmallSelect
              fullWidth
              displayEmpty
              aria-label={
                idx === 0 ? 'Pass Route Destination' : 'Fail Route Destination'
              }
              MenuProps={SMALL_MENU_PROPS}
              value={id || ''}
              renderValue={(v) => (v ? labelFor(v) : 'Select')}
              sx={{
                ...sharedInputSx,
                mb: 0.5,
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
              onChange={(e) => {
                const v = e.target.value;
                setCurrentFlow((prev) => {
                  const next = [...prev];
                  const other = next.findIndex((x) => x === v);
                  if (other !== -1 && other !== idx)
                    [next[idx], next[other]] = [v, next[idx] || ''];
                  else next[idx] = v;
                  return next;
                });
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {/*{destOptions.map((o, i) => (
                <SmallMenuItem key={o.id} value={o.id}>
                  {o.label} D{i + 1}
                </SmallMenuItem>
              ))} */}

              {destOptions.map((o) => {
                const name = destDisplayById[o.id] || o.label;
                return (
                  <SmallMenuItem key={o.id} value={o.id}>
                    {name}
                  </SmallMenuItem>
                );
              })}
            </SmallSelect>
          </Box>
        ))}
      </Box>

      {errorMsg && (
        <Chip
          label={errorMsg}
          color="error"
          size="small"
          sx={{ mt: 1, fontWeight: 500 }}
        />
      )}

      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, opacity: 0.8, mb: 0.5 }}>
          Checklist
        </Typography>

        <List dense sx={{ py: 0 }}>
          {requiredEntities.map((ent) => {
            const ok = conditions.some(
              (c) => c.sourceEntity === ent && isConditionComplete(c),
            );
            return (
              <ListItem key={ent} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 22 }}>
                  {ok ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <HighlightOffIcon fontSize="small" color="error" />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={<Typography sx={{ fontSize: 12 }}>{ent}</Typography>}
                  secondary={ok ? 'Covered' : 'Missing'}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: 11,
                      color: ok ? 'success.main' : 'error.main',
                    },
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 2,
          mt: 'auto',
          mb: 2,
        }}
      >
        <Tooltip title="Save Condition">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            sx={getContainedButtonStyle}
          >
            Save
          </Button>
        </Tooltip>

        <Tooltip title="Save and Next">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleCloseAndSave}
            sx={{
              borderRadius: '0.5rem',
              px: 2,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: darkMode
                  ? 'rgba(58, 142, 255, 0.1)'
                  : 'rgba(0, 86, 210, 0.1)',
              },
            }}
            disabled={!canSaveNext}
          >
            Save & Next
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

DecisionConfigurationModal.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    data: PropTypes.shape({
      label: PropTypes.string,
      config: PropTypes.object,
    }).isRequired,
  }),
  setSelectedNode: PropTypes.func.isRequired,
  updateNodeData: PropTypes.func.isRequired,
  onConfigUpdate: PropTypes.func.isRequired,
  sourceResolved: PropTypes.array.isRequired,
  onSaveAndNext: PropTypes.func.isRequired,
  destinationOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ),
  connectedDestinations: PropTypes.shape({
    destIds: PropTypes.arrayOf(PropTypes.string),
    destNames: PropTypes.arrayOf(PropTypes.string),
  }),
  onClose: PropTypes.func.isRequired,
};

export default DecisionConfigurationModal;
